import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Self-reported acquisition source, asked (optionally) on the signup screen and
// stored on users/{uid}.foundVia. Distinct from `signupSource` (auto-derived
// from UTM / referrer) — this captures channels no URL can, like word-of-mouth
// and App Store browse. Keys must match FOUND_VIA_OPTIONS values in
// packages/shared/src/utils/constants.js. This chart owns the display order,
// labels, and colors (the rollup buckets whatever value it finds).
const FOUND_VIA_ORDER = [
  { key: 'friend', label: 'Friend / co-parent', color: '#1B6B4A' },
  { key: 'google', label: 'Google search', color: '#F5C842' },
  { key: 'app_store', label: 'App Store', color: '#3BA676' },
  { key: 'instagram', label: 'Instagram', color: '#F2886D' },
  { key: 'threads', label: 'Threads', color: '#1E2328' },
  { key: 'blog', label: 'Blog post', color: '#7BC3D8' },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
  // Filter out zero-value series so the tooltip stays readable.
  const visible = payload.filter((p) => (p.value || 0) > 0);
  return (
    <div className="bg-white rounded-xl px-3 py-2 shadow-warm-md border border-surface-200 text-sm space-y-1">
      <p className="font-semibold text-ink-800">{label}</p>
      <p className="text-xs text-ink-500 font-medium">Total: {total}</p>
      {visible.map((p) => (
        <p key={p.dataKey} className="text-ink-500 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span>{p.name}:</span>
          <span>{p.value?.toLocaleString() ?? 0}</span>
        </p>
      ))}
    </div>
  );
};

const FoundViaChart = ({ stats }) => {
  const data = useMemo(() => {
    return (stats || []).map((s) => {
      const byFoundVia = s.newAccountsByFoundVia || {};
      const row = { date: s.date?.slice(5) || s.id?.slice(5) };
      for (const { key } of FOUND_VIA_ORDER) {
        row[key] = byFoundVia[key] || 0;
      }
      return row;
    });
  }, [stats]);

  // Only render legend entries for sources that actually have data in the
  // window — otherwise the legend shows every bucket on day 1.
  const activeSources = useMemo(() => {
    return FOUND_VIA_ORDER.filter(({ key }) => data.some((row) => row[key] > 0));
  }, [data]);

  const totalInWindow = useMemo(() => {
    return data.reduce((sum, row) => {
      return sum + FOUND_VIA_ORDER.reduce((acc, { key }) => acc + (row[key] || 0), 0);
    }, 0);
  }, [data]);

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-ink-900 text-[0.9375rem]">How users found us</h3>
        <span className="text-xs text-ink-400">Last 30 days · {totalInWindow} answered</span>
      </div>
      <p className="text-xs text-ink-400 mb-5">
        Self-reported at signup (optional). Complements the auto-derived “Signups by source”.
      </p>
      {totalInWindow > 0 ? (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d6" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#57534e' }} axisLine={{ stroke: '#e5e0d6' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#57534e' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(27, 107, 74, 0.05)' }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            {activeSources.map(({ key, label, color }, i) => (
              <Bar
                key={key}
                dataKey={key}
                name={label}
                stackId="a"
                fill={color}
                // Round only the topmost stack segment for visual polish.
                radius={i === activeSources.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="border-2 border-dashed border-surface-200 rounded-xl p-8 text-center">
          <p className="text-sm text-ink-500 mb-1">No self-reported sources yet.</p>
          <p className="text-xs text-ink-400">
            New signups populate this once the “How did you find us?” question ships. Historical
            accounts have no answer, so only new signups appear here.
          </p>
        </div>
      )}
    </div>
  );
};

export default FoundViaChart;
